import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { FormService } from './form.service';

export interface AdviceReportProjectInfo {
    grants_agreement_number?: string;
    project_acronym?: string;
    full_project_name?: string;
    project_website?: string;
}

@Injectable({
    providedIn: 'root'
})
export class AdvicePdfService {

    export(projectInfo: AdviceReportProjectInfo, formService: FormService, sActions: number[], defaultAction: number[]): void {
        const pdf = new jsPDF();
        const marginLeft = 10;
        let x = 10;
        let y = 60;
        let isLinkInserted = false;
        const pageHeight = pdf.internal.pageSize.getHeight();

        const increaseY = (value: number): void => {
            if (isLinkInserted) {
                x = marginLeft;
                isLinkInserted = false;
            }
            y += value;
        };

        const increaseX = (amount: number): void => {
            x += amount;
        };

        const setX = (value: number): void => {
            x = value;
        };

        const addPageIfNecessary = (): void => {
            if (y >= pageHeight - 10) {
                pdf.addPage();
                y = 20;
            }
        };

        const addText = (htmlString: string): void => {
            const textBoxWidth = 205;
            const div = document.createElement('div');
            div.innerHTML = htmlString;

            const processNode = (node: ChildNode) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.textContent?.trim();
                    if (text) {
                        const lines = pdf.splitTextToSize(text, textBoxWidth - x);
                        lines.forEach((line: string) => {
                            addPageIfNecessary();
                            pdf.setFont('helvetica', 'normal');
                            pdf.text(line, x, y);
                            increaseY(7);
                        });
                    }
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    const el = node as HTMLElement;
                    switch (el.tagName.toLowerCase()) {
                        case 'p':
                            Array.from(el.childNodes).forEach(processNode);
                            increaseY(10);
                            break;
                        case 'ul':
                            Array.from(el.children).forEach((li) => {
                                if (li.tagName.toLowerCase() === 'li') {
                                    addPageIfNecessary();
                                    pdf.setFont('helvetica', 'normal');

                                    const bullet = '• ';
                                    pdf.text(bullet, x, y);

                                    const savedX = x;
                                    increaseX(pdf.getTextWidth(bullet));

                                    Array.from(li.childNodes).forEach(processNode);

                                    setX(savedX);
                                    increaseY(7);
                                }
                            });
                            increaseY(5);
                            break;
                        case 'strong':
                            pdf.setFont('helvetica', 'bold');
                            Array.from(el.childNodes).forEach(processNode);
                            pdf.setFont('helvetica', 'normal');
                            break;
                        case 'a':
                            const linkText = el.textContent?.trim();
                            if (linkText) {
                                const lines = pdf.splitTextToSize(linkText, textBoxWidth - x);
                                pdf.setTextColor(0, 0, 255);
                                lines.forEach((line: string) => {
                                    addPageIfNecessary();
                                    const anchor = el as HTMLAnchorElement;
                                    pdf.textWithLink(line, x, y, { url: anchor.href });
                                    isLinkInserted = true;
                                    x += pdf.getTextWidth(line) + 1;
                                });
                                pdf.setTextColor(0, 0, 0);
                            }
                            break;
                        default:
                            Array.from(el.childNodes).forEach(processNode);
                    }
                }
            };

            Array.from(div.childNodes).forEach(processNode);
        };

        const img = new Image();
        img.src = 'assets/img/logo.png';
        pdf.addImage(img, 'PNG', 10, 10, 60, 20);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(20);
        pdf.text('Advice Report', 10, 45);
        pdf.setFontSize(10);

        if (projectInfo.grants_agreement_number || projectInfo.project_acronym ||
            projectInfo.full_project_name || projectInfo.project_website) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(12);
            pdf.text('Project Information', x, y);
            pdf.setFontSize(10);
            increaseY(7);

            if (projectInfo.grants_agreement_number) {
                pdf.setFont('helvetica', 'bold');
                pdf.text('Grant Agreement Number:', x, y);
                x += 50;
                addText(projectInfo.grants_agreement_number);
                x -= 50;
            }
            if (projectInfo.project_acronym) {
                pdf.setFont('helvetica', 'bold');
                pdf.text('Project Acronym:', x, y);
                x += 50;
                addText(projectInfo.project_acronym);
                x -= 50;
            }
            if (projectInfo.full_project_name) {
                pdf.setFont('helvetica', 'bold');
                pdf.text('Full Project Name:', x, y);
                x += 50;
                addText(projectInfo.full_project_name);
                x -= 50;
            }
            if (projectInfo.project_website) {
                pdf.setFont('helvetica', 'bold');
                pdf.text('Project Website:', x, y);
                x += 50;
                addText(projectInfo.project_website);
                x -= 50;
            }
        }

        pdf.setFont('helvetica', 'bold');
        pdf.text('TRL of the project:', x, y);
        x += 50;
        switch (formService.statement) {
            case 'A':
                addText('TRL 1-3 (Basic Research)');
                break;
            case 'B':
                addText('TRL 4-5 (Applied Research)');
                break;
            case 'C':
                addText('TRL 6-7 (Demonstration & Implementation)');
                break;
        }
        x -= 50;

        formService.getQuestionKeysByStatement().forEach(key => {
            if (formService.getAnswersByMatrixAnswerOfQuestionKey(key).length > 0) {
                addPageIfNecessary();
                pdf.setFont('helvetica', 'bold');
                pdf.text(formService.getQuestionByKey(key), x, y);
                increaseY(7);
                for (let i = 0; i < formService.getAnswersByMatrixAnswerOfQuestionKey(key).length; i++) {
                    addText(i + 1 + ') ' + formService.getAnswersByMatrixAnswerOfQuestionKey(key)[i]);
                }
            }
        });
        increaseY(13);
        addPageIfNecessary();
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text('Specific Advice', x, y);
        pdf.setFontSize(10);
        increaseY(7);

        for (let i = 0; i < sActions.length; i++) {
            addPageIfNecessary();
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Advice ${i + 1}: ${formService.getActionById(sActions[i])[0]}`, x, y);
            increaseY(7);
            addText(formService.getActionById(sActions[i])[1]);
            increaseY(3);
        }

        addPageIfNecessary();
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.text('General Advice', x, y);
        pdf.setFontSize(10);
        increaseY(7);

        for (let i = 0; i < defaultAction.length; i++) {
            addPageIfNecessary();
            pdf.setFont('helvetica', 'bold');
            pdf.text(`Advice ${i + 1 + sActions.length}: ${formService.getActionById(defaultAction[i])[0]}`, x, y);
            increaseY(7);
            addText(formService.getActionById(defaultAction[i])[1]);
            increaseY(3);
        }

        pdf.save('advice_report.pdf');
    }
}
